import { useMemo, useState } from 'react';

import type {
  GetPendingRefillsDetailedParams,
  GetRefillDetailedResponseT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { usePendingRefillsDetailedQuery } from '@/entities/rx';
import { toTableCellText } from '@/shared/lib/table';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import { Card, CardContent } from '@/shared/ui/shadcn/card';

import { buildMedicationLabel } from '../lib';
import { useWorklistPagination } from '../model';
import { RefillDetailSheet } from './RefillDetailSheet';
import { WorklistPagination } from './WorklistPagination';
import { WorklistEmpty, WorklistError, WorklistLoading } from './WorklistStates';

export interface PendingRefillsListProps {
  readonly patientId?: number | null;
}

function getRefillRowKey(refill: GetRefillDetailedResponseT, index: number): string {
  return refill.RefillId !== null && refill.RefillId !== undefined
    ? String(refill.RefillId)
    : `refill-${index}`;
}

export function PendingRefillsList({ patientId }: PendingRefillsListProps) {
  const { pageNumber, nextPage, previousPage } = useWorklistPagination(1);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const params = useMemo<GetPendingRefillsDetailedParams>(
    () => ({ patientId: patientId ?? undefined, pageNumber }),
    [patientId, pageNumber]
  );

  const refillsQuery = usePendingRefillsDetailedQuery(params);

  const items = useMemo<ReadonlyArray<GetRefillDetailedResponseT>>(
    () => refillsQuery.data?.Items ?? [],
    [refillsQuery.data]
  );
  const pageResult = refillsQuery.data?.PageResult ?? null;

  const selectedRefill = useMemo<GetRefillDetailedResponseT | null>(() => {
    if (selectedKey === null) return null;
    const match = items.find((refill, index) => getRefillRowKey(refill, index) === selectedKey);
    return match ?? null;
  }, [items, selectedKey]);

  if (refillsQuery.isPending) {
    return <WorklistLoading label="Loading pending refills…" />;
  }

  if (refillsQuery.isError) {
    return (
      <WorklistError
        title="Could not load pending refills"
        error={refillsQuery.error}
        onRetry={() => {
          void refillsQuery.refetch();
        }}
      />
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <WorklistEmpty
          label="No pending refill requests."
          hint="Requests from pharmacies appear here after DoseSpot forwards them to Lotus Health."
        />
        <WorklistPagination
          pageNumber={pageNumber}
          pageResult={pageResult}
          onPrevious={previousPage}
          onNext={nextPage}
          isFetching={refillsQuery.isFetching}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col gap-2" aria-label="Pending refill requests">
        {items.map((refill, index) => {
          const medication = buildMedicationLabel(
            refill.RequestedPrescription,
            refill.OriginalPrescription ?? refill.DispensedPrescription
          );
          const requestedDate = toTableCellText(refill.RequestedDate);
          const pharmacyLabel = toTableCellText(refill.PharmacyId);
          const refillIdLabel = toTableCellText(refill.RefillId);
          const rowKey = getRefillRowKey(refill, index);

          return (
            <li key={rowKey}>
              <Card>
                <CardContent className="flex flex-col gap-2 px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{medication}</div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Badge variant="secondary" className="rounded-full">
                        Refill #{refillIdLabel}
                      </Badge>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedKey(rowKey);
                        }}
                        aria-label={`View refill ${refillIdLabel} details`}
                      >
                        View details
                      </Button>
                    </div>
                  </div>
                  <div className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    <span>Requested {requestedDate}</span>
                    <span>Pharmacy {pharmacyLabel}</span>
                  </div>
                </CardContent>
              </Card>
            </li>
          );
        })}
      </ul>

      <WorklistPagination
        pageNumber={pageNumber}
        pageResult={pageResult}
        onPrevious={previousPage}
        onNext={nextPage}
        isFetching={refillsQuery.isFetching}
      />

      <RefillDetailSheet
        refill={selectedRefill}
        open={selectedRefill !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setSelectedKey(null);
        }}
      />
    </div>
  );
}
